import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink, updatePassword } from 'firebase/auth';
import { auth } from './firebase';
import logo from '../assets/logos/KUROCO_left corner_logo.png';

// Module scope on purpose: defined inside the page, React re-created it on every keystroke,
// unmounting the input — iOS Safari then hides the keyboard after one character (2026-10-23).
const Card = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-6 font-['Noto_Sans_JP',Helvetica,sans-serif] bg-[#E4E7E6]">
    <div className="w-full max-w-[420px] rounded-xl shadow-lg p-8 md:p-10 bg-white border border-gray-200">
      <div className="flex justify-center mb-6">
        <div className="inline-flex px-5 py-3 bg-gray-50 rounded-lg">
          <img src={logo} alt="KUROCO" className="h-7 object-contain" />
        </div>
      </div>
      {children}
    </div>
  </div>
);

const InviteCallbackPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // loading | enter_email | set_password | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setStep('error');
      setError('無効な招待リンクです。管理者に再送付を依頼してください。');
      return;
    }
    // Firebase embeds email in the link — extract it
    const params = new URLSearchParams(window.location.search);
    const emailFromLink = params.get('email') || '';
    if (emailFromLink) {
      setEmail(emailFromLink);
      handleSignIn(emailFromLink);
    } else {
      setStep('enter_email');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignIn = async (resolvedEmail) => {
    setStep('loading');
    try {
      await signInWithEmailLink(auth, resolvedEmail, window.location.href);
      setEmail(resolvedEmail);
      setStep('set_password');
    } catch (err) {
      setStep('error');
      setError(
        err.code === 'auth/invalid-action-code'
          ? '招待リンクが期限切れまたは無効です。管理者に再送付を依頼してください。'
          : err.message
      );
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    await handleSignIn(email.trim().toLowerCase());
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return;
    }
    if (password.length < 8) {
      setError('パスワードは8文字以上で設定してください。');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await updatePassword(auth.currentUser, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'loading') {
    return (
      <Card>
        <p className="text-center text-gray-500 text-sm">認証中...</p>
      </Card>
    );
  }

  if (step === 'error') {
    return (
      <Card>
        <h1 className="text-[#1a1a1a] text-center text-lg font-semibold mb-4">リンクが無効です</h1>
        <p className="text-red-700 text-sm text-center bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </p>
      </Card>
    );
  }

  if (step === 'enter_email') {
    return (
      <Card>
        <h1 className="text-[#1a1a1a] text-center text-lg font-semibold mb-2">メールアドレスの確認</h1>
        <p className="text-gray-500 text-center text-sm mb-6">
          招待されたメールアドレスを入力してください。
        </p>
        {error && (
          <div className="mb-4 p-3 rounded-lg text-[13px] text-red-700 bg-red-50 border border-red-200">{error}</div>
        )}
        <form onSubmit={handleEmailSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="メールアドレス"
            required
            className="w-full px-4 py-3 text-sm rounded-lg border border-gray-300 bg-gray-50 text-[#1a1a1a] placeholder:text-gray-400 focus:border-[#33564B] focus:bg-white outline-none mb-4"
          />
          <button
            type="submit"
            className="w-full py-3.5 text-[15px] font-semibold text-white rounded-lg bg-[#33564B] hover:bg-[#2a463d]"
          >
            確認
          </button>
        </form>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="text-[#1a1a1a] text-center text-lg font-semibold tracking-widest uppercase mb-2">
        アカウント設定
      </h1>
      <p className="text-gray-500 text-center text-sm mb-1">ようこそ</p>
      <p className="text-[#33564B] text-center text-sm font-medium mb-6">{email}</p>

      {error && (
        <div className="mb-5 p-3 rounded-lg text-[13px] text-red-700 bg-red-50 border border-red-200">{error}</div>
      )}

      <form onSubmit={handlePasswordSubmit}>
        <div className="mb-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="パスワード（8文字以上）"
              required
              className="w-full px-4 py-3 pr-11 text-sm rounded-lg border border-gray-300 bg-gray-50 text-[#1a1a1a] placeholder:text-gray-400 focus:border-[#33564B] focus:bg-white outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-gray-500 hover:text-gray-700"
            >
              {showPassword ? '非表示' : '表示'}
            </button>
          </div>
        </div>
        <div className="mb-6">
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="パスワード（確認）"
            required
            className="w-full px-4 py-3 text-sm rounded-lg border border-gray-300 bg-gray-50 text-[#1a1a1a] placeholder:text-gray-400 focus:border-[#33564B] focus:bg-white outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3.5 text-[15px] font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-2 bg-[#33564B] hover:bg-[#2a463d]"
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 16 16">
                <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" />
                <path d="M8 2a6 6 0 0 1 6 6" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
              </svg>
              設定中...
            </>
          ) : (
            'パスワードを設定してログイン'
          )}
        </button>
      </form>
    </Card>
  );
};

export default InviteCallbackPage;
